import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { newAccountServiceContext, SSOService } from '@vendasta/sso';
import { BehaviorSubject, combineLatest, map, Observable, take } from 'rxjs';
import { AccountService } from '../../../core/account.service';
import { PartnerService } from '../../../core/partner.service';
import { User } from '../../../core/user';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { GalaxyConfirmationModalModule } from '@vendasta/galaxy/confirmation-modal';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';

@Component({
  imports: [
    CommonModule,
    TranslateModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatInputModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    GalaxyConfirmationModalModule,
    MatButtonModule,
    MatIconModule,
    MatDialogClose,
  ],
  styleUrls: ['./impersonate-user-dialog.component.scss'],
  templateUrl: './impersonate-user-dialog.component.html',
})
export class ImpersonateUserDialogComponent implements OnInit {
  selectedUserFormControl = new FormControl<User | string>({ value: '', disabled: true }, Validators.required);
  impersonateableUsersSubject: BehaviorSubject<User[]> = new BehaviorSubject([]);

  associatedUserIds: string[];
  loading = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { accountGroupId: string; companyName: string },
    public dialogRef: MatDialogRef<ImpersonateUserDialogComponent>,
    private accountService: AccountService,
    private accountGroupService: AccountGroupService,
    private partnerService: PartnerService,
    private ssoService: SSOService,
    private snackbarService: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.accountService.loadUsers(this.data.accountGroupId, this.partnerService.partnerId).subscribe((users) => {
      let impersonatableUsers: User[] = [];
      if (users && users.length > 0) {
        impersonatableUsers = this.filterUsersByImpersonable(users);
      }
      if (impersonatableUsers.length > 0) {
        this.loading = false;
        this.impersonateableUsersSubject.next(impersonatableUsers);
        this.selectedUserFormControl.enable();
      }
    });
    this.accountGroupService
      .get(this.data.accountGroupId, new ProjectionFilter({ associations: true }))
      .pipe(take(1))
      .subscribe((accountGroup) => {
        this.associatedUserIds = accountGroup.associations.map((association) => association.vbcUserId);
      });
  }

  get impersonateableUsers$(): Observable<User[]> {
    return this.impersonateableUsersSubject.asObservable();
  }

  filteredOptions$ = combineLatest([this.impersonateableUsers$, this.selectedUserFormControl.valueChanges]).pipe(
    map(([users, value]) =>
      users.filter((u) => {
        if (value instanceof User) {
          return u.descriptiveName?.includes(value?.descriptiveName ?? '');
        }
        return u.descriptiveName?.includes(value ?? '');
      }),
    ),
  );

  filterUsersByImpersonable(users: User[]): User[] {
    return users.filter((user) => this.associatedUserIds.indexOf(user.userId) > -1);
  }

  impersonateUser(): void {
    this.selectedUserFormControl.markAsTouched();
    if (this.selectedUserFormControl.invalid) {
      return;
    }
    if (!(this.selectedUserFormControl.value instanceof User)) {
      this.selectedUserFormControl.setValue('');
      return;
    }
    const selectedUser = this.selectedUserFormControl.value;
    this.ssoService
      .getEntryUrl('VBC', newAccountServiceContext(this.data.accountGroupId), {
        impersonation: selectedUser.unifiedUserId,
        path: `/account/location/${this.data.accountGroupId}/dashboard`,
      })
      .subscribe({
        next: (url) => {
          window.open(url, '_blank');
          this.dialogRef.close();
        },
        error: () => this.snackbarService.openErrorSnack('MANAGE_ACCOUNTS.IMPERSONATE_USER_DIALOG.ERROR'),
      });
    this.dialogRef.close();
  }

  displayFn(user: User): string {
    return user?.descriptiveName ?? '';
  }
}
